import React, { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { expenseService, invoiceService } from '../services/api';
import { Expense, Invoice } from '../types';
import { normalizePaymentType } from '../utils/paymentDisplay';
import { creatorDisplayName } from '../utils/creatorDisplay';
import './ExpenseManagement.css';

const CATEGORIES: { value: string; label: string }[] = [
  { value: 'STAFF_SALARY', label: 'Staff Salary' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'SUPPLIES', label: 'Supplies' },
  { value: 'FOOD', label: 'Food' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'OTHERS', label: 'Others' },
];

const PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'OTHERS', label: 'Others' },
];

const AMOUNT_CLASSES = ['expenses-amount--g', 'expenses-amount--r', 'expenses-amount--y', 'expenses-amount--b', 'expenses-amount--p'];

function categoryLabel(cat?: string): string {
  return CATEGORIES.find((c) => c.value === cat)?.label || cat || '—';
}

function paymentMethodLabel(method?: string): string {
  const key = String(method ?? '')
    .trim()
    .toUpperCase();
  if (key === 'CASH') return 'Cash';
  if (key === 'UPI' || key === 'BANK_TRANSFER') return 'UPI';
  if (key === 'OTHERS' || key === 'OTHER' || key === 'CREDIT_CARD' || key === 'CHECK') return 'Others';
  return PAYMENT_METHODS.find((m) => m.value === key)?.label || method || '—';
}

function formatExpenseDate(val?: string): string {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getExpensePhotoUrl(expense: Expense): string | null {
  const att = expense.attachments;
  if (!att || !Array.isArray(att) || att.length === 0) return null;
  const first = att[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object' && 'url' in first && first.url) return first.url;
  return null;
}

function sanitizeFilePart(value: string, maxLen = 48): string {
  const s = value
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return (s || 'expense').slice(0, maxLen);
}

function expenseImageTimestamp(expense: Expense): string {
  const raw = expense.expenseDate || expense.createdAt;
  if (!raw) return String(expense.id);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(expense.id);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

function expenseImageBaseName(expense: Expense): string {
  const title = sanitizeFilePart(expense.title || 'expense');
  const amount = Number(expense.amount || 0).toFixed(2).replace(/\./g, '_');
  return `${title}_${amount}_${expenseImageTimestamp(expense)}`;
}

function imageExtFromBlob(blob: Blob, url: string): string {
  if (blob.type.includes('png')) return 'png';
  if (blob.type.includes('webp')) return 'webp';
  if (blob.type.includes('gif')) return 'gif';
  const m = url.match(/\.(jpe?g|png|webp|gif)(\?|$)/i);
  if (m) return m[1].toLowerCase().replace('jpeg', 'jpg');
  return 'jpg';
}

function emptyForm() {
  return {
    title: '',
    description: '',
    category: 'MAINTENANCE',
    amount: '',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'UPI',
  };
}

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function monthBounds(year: number, month: number): { start: string; end: string } {
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return { start: toISODate(start), end: toISODate(end) };
}

const YEAR_START = 2026;

function buildYearOptions(): string[] {
  const y = Math.max(new Date().getFullYear(), YEAR_START);
  const years: string[] = [];
  for (let i = YEAR_START; i <= y + 1; i++) years.push(String(i));
  return years;
}

function invoiceInDateRange(inv: Invoice, startDate: string, endDate: string): boolean {
  const ts = inv.paymentTimestamp ? new Date(inv.paymentTimestamp).getTime() : NaN;
  if (!Number.isFinite(ts)) return false;
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  return ts >= start.getTime() && ts <= end.getTime();
}

function sumCollectionForPeriod(invoices: Invoice[], hostelId: number, startDate: string, endDate: string): number {
  return invoices
    .filter((inv) => {
      if (inv.isDeleted) return false;
      if (inv.hostelId != null && Number(inv.hostelId) !== hostelId) return false;
      if (!invoiceInDateRange(inv, startDate, endDate)) return false;
      if (normalizePaymentType(inv.paymentType) === 'REFUND') return false;
      return true;
    })
    .reduce((sum, inv) => sum + parseFloat(String(inv.paidAmount || 0)), 0);
}

function computeDateRange(
  filterYear: string,
  filterMonth: string,
  rangeStart: string,
  rangeEnd: string,
): { startDate: string; endDate: string; label: string } {
  if (rangeStart && rangeEnd) {
    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
      return {
        startDate: rangeEnd,
        endDate: rangeStart,
        label: `${formatExpenseDate(rangeEnd)} – ${formatExpenseDate(rangeStart)}`,
      };
    }
    return {
      startDate: rangeStart,
      endDate: rangeEnd,
      label: `${formatExpenseDate(rangeStart)} – ${formatExpenseDate(rangeEnd)}`,
    };
  }

  const year = parseInt(filterYear, 10) || new Date().getFullYear();
  const month = parseInt(filterMonth, 10);
  if (filterMonth && month >= 1 && month <= 12) {
    const { start, end } = monthBounds(year, month);
    const label = new Date(year, month - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
    return { startDate: start, endDate: end, label };
  }

  return {
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
    label: String(year),
  };
}

function currentMonthDefaults() {
  const now = new Date();
  const year = Math.max(now.getFullYear(), YEAR_START);
  return {
    year: String(year),
    month: String(now.getMonth() + 1).padStart(2, '0'),
  };
}

const ExpenseManagement: React.FC = () => {
  const { hostel, user } = useAuth();
  const monthDefaults = currentMonthDefaults();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterYear, setFilterYear] = useState(monthDefaults.year);
  const [filterMonth, setFilterMonth] = useState(monthDefaults.month);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [newExpense, setNewExpense] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const closeCreateModal = () => {
    if (submitting) return;
    clearPhoto();
    setShowCreate(false);
  };

  const dateRange = useMemo(
    () => computeDateRange(filterYear, filterMonth, rangeStart, rangeEnd),
    [filterYear, filterMonth, rangeStart, rangeEnd],
  );

  const yearOptions = useMemo(() => buildYearOptions(), []);

  const loadExpenses = async () => {
    if (!hostel?.id) return;
    try {
      setLoading(true);
      const filters: Record<string, string> = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      if (filterCategory !== 'ALL') filters.category = filterCategory;
      const data = await expenseService.getExpensesByHostel(hostel.id, filters);
      setExpenses(data);
    } catch {
      toast.error('Failed to load expenses');
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await invoiceService.getAllInvoices();
      setInvoices(data);
    } catch {
      setInvoices([]);
    }
  };

  useEffect(() => {
    if (!hostel?.id) return;
    void loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostel?.id]);

  useEffect(() => {
    void loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostel?.id, filterCategory, filterYear, filterMonth, rangeStart, rangeEnd]);

  useEffect(() => {
    setPage(1);
  }, [query, filterCategory, filterYear, filterMonth, rangeStart, rangeEnd, pageSize]);

  const resetToCurrentMonth = () => {
    const d = currentMonthDefaults();
    setFilterYear(d.year);
    setFilterMonth(d.month);
    setRangeStart('');
    setRangeEnd('');
  };

  const onYearChange = (year: string) => {
    setFilterYear(year);
    setRangeStart('');
    setRangeEnd('');
  };

  const onMonthChange = (month: string) => {
    setFilterMonth(month);
    setRangeStart('');
    setRangeEnd('');
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return expenses;
    return expenses.filter((e) => {
      const hay = [
        e.title,
        categoryLabel(e.category),
        paymentMethodLabel(e.paymentMethod),
        e.creator?.username,
        String(e.id),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [expenses, query]);

  const kpis = useMemo(() => {
    const sum = (list: Expense[]) => list.reduce((s, e) => s + Number(e.amount || 0), 0);
    const byCategory: Record<string, number> = {};
    expenses.forEach((e) => {
      const key = e.category || 'OTHERS';
      byCategory[key] = (byCategory[key] || 0) + Number(e.amount || 0);
    });
    let topCategory = { key: '', amount: 0 };
    Object.entries(byCategory).forEach(([key, amount]) => {
      if (amount > topCategory.amount) topCategory = { key, amount };
    });
    const total = sum(expenses);
    const collection =
      hostel?.id != null
        ? sumCollectionForPeriod(invoices, hostel.id, dateRange.startDate, dateRange.endDate)
        : 0;
    const difference = collection - total;
    const expenseRatioPct = collection > 0 ? (total / collection) * 100 : null;
    return {
      total,
      count: expenses.length,
      collection,
      difference,
      expenseRatioPct,
      topCategory,
    };
  }, [expenses, invoices, hostel?.id, dateRange.startDate, dateRange.endDate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const pageNumbers = useMemo(() => {
    const max = Math.min(totalPages, 10);
    const nums: number[] = [];
    for (let i = 1; i <= max; i++) nums.push(i);
    return nums;
  }, [totalPages]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (pageItems.every((e) => selectedIds.has(e.id))) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageItems.forEach((e) => next.delete(e.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageItems.forEach((e) => next.add(e.id));
        return next;
      });
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id || !user?.id) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('hostelId', String(hostel.id));
      formData.append('createdBy', String(user.id));
      formData.append('title', newExpense.title);
      formData.append('description', newExpense.description);
      formData.append('category', newExpense.category);
      formData.append('amount', String(parseFloat(newExpense.amount)));
      formData.append('expenseDate', newExpense.expenseDate);
      formData.append('paymentMethod', newExpense.paymentMethod);
      if (photoFile) formData.append('photo', photoFile);

      await expenseService.createExpense(formData);
      toast.success('Expense recorded');
      setNewExpense(emptyForm());
      clearPhoto();
      setShowCreate(false);
      await loadExpenses();
    } catch (err: unknown) {
      const er = err as { response?: { data?: { message?: string } } };
      toast.error(er?.response?.data?.message || 'Failed to create expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this expense? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await expenseService.deleteExpense(id);
      toast.success('Expense deleted');
      setSelectedExpense(null);
      await loadExpenses();
    } catch {
      toast.error('Failed to delete expense');
    } finally {
      setDeleting(false);
    }
  };

  const handleExportImagesZip = async () => {
    if (!hostel) return;
    setUploadOpen(false);

    const withPhotos = expenses.filter((e) => getExpensePhotoUrl(e));
    if (withPhotos.length === 0) {
      toast.error('No receipt images in the selected period');
      return;
    }

    setExportingZip(true);
    try {
      const zip = new JSZip();
      const usedNames = new Set<string>();
      let added = 0;

      for (const expense of withPhotos) {
        const url = getExpensePhotoUrl(expense)!;
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const blob = await res.blob();
          const ext = imageExtFromBlob(blob, url);
          let fileName = `${expenseImageBaseName(expense)}.${ext}`;
          if (usedNames.has(fileName)) {
            fileName = `${expenseImageBaseName(expense)}_${expense.id}.${ext}`;
          }
          usedNames.add(fileName);
          zip.file(fileName, blob);
          added += 1;
        } catch {
          // skip failed fetch (CORS/network)
        }
      }

      if (added === 0) {
        toast.error('Could not download images. Check your connection or try again.');
        return;
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const safePeriod = dateRange.label.replace(/[^\w\-]+/g, '_');
      const safeHostel = (hostel.name || 'hostel').replace(/[^\w\-]+/g, '_');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = `${safeHostel}_receipts_${safePeriod}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(`Downloaded ${added} receipt image${added === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to create ZIP file');
    } finally {
      setExportingZip(false);
    }
  };

  const handleExportPdf = () => {
    if (!hostel) return;
    const exportRows = query.trim() ? filtered : expenses;
    if (exportRows.length === 0) {
      toast.error('No expenses to export');
      return;
    }
    setUploadOpen(false);
    try {
      const head = ['Title', 'Account', 'Date', 'Payment', 'Recorded by', 'Amount'];
      const body = exportRows.map((e) => [
        e.title || '—',
        categoryLabel(e.category),
        formatExpenseDate(e.expenseDate),
        paymentMethodLabel(e.paymentMethod),
        e.creator?.username || '—',
        `₹${Number(e.amount || 0).toLocaleString('en-IN')}`,
      ]);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(11);
      doc.text(`${hostel.name} — Expenses (${dateRange.label})`, 40, 28);
      autoTable(doc, {
        startY: 38,
        head: [head],
        body,
        styles: { fontSize: 7, cellPadding: 2.5 },
        headStyles: { fillColor: [22, 163, 74], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 28, right: 28 },
      });
      doc.save(`${(hostel.name || 'expenses').replace(/[^\w\-]+/g, '_')}-expenses.pdf`);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  if (!hostel) {
    return <div className="expenses-loading">Loading…</div>;
  }

  return (
    <div className="expenses-page">
      <header className="expenses-page-header">
        <div className="expenses-page-title-wrap">
          <h1 className="expenses-page-title">All Expenses</h1>
          <p className="expenses-period-label">Showing: {dateRange.label}</p>
        </div>
        <div className="expenses-page-actions">
          <div className="expenses-dropdown">
            <button type="button" className="expenses-btn" onClick={() => setUploadOpen((o) => !o)}>
              Export ▾
            </button>
            {uploadOpen ? (
              <div className="expenses-dropdown-menu">
                <button type="button" className="expenses-dropdown-item" onClick={handleExportPdf}>
                  Export PDF
                </button>
                <button
                  type="button"
                  className="expenses-dropdown-item"
                  disabled={exportingZip}
                  onClick={() => void handleExportImagesZip()}
                >
                  {exportingZip ? 'Preparing ZIP…' : 'Download receipt images (ZIP)'}
                </button>
              </div>
            ) : null}
          </div>
          <button type="button" className="expenses-btn expenses-btn--primary" onClick={() => setShowCreate(true)}>
            + Add New
          </button>
        </div>
      </header>

      <section className="expenses-stat-row" aria-label="Expense summary">
        <article className="expenses-stat-card">
          <div className="expenses-stat-card-top">
            <div className="expenses-stat-icon expenses-stat-icon--green" aria-hidden>
              ₹
            </div>
            <button type="button" className="expenses-stat-menu" aria-label="Options">
              ⋯
            </button>
          </div>
          <p className="expenses-stat-label">Total Expenses</p>
          <p className="expenses-stat-value">₹{kpis.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          <p className="expenses-stat-trend expenses-stat-trend--up">↗ {kpis.count} in selected period</p>
        </article>
        <article className="expenses-stat-card">
          <div className="expenses-stat-card-top">
            <div className="expenses-stat-icon expenses-stat-icon--blue" aria-hidden>
              ◷
            </div>
            <button type="button" className="expenses-stat-menu" aria-label="Options">
              ⋯
            </button>
          </div>
          <p className="expenses-stat-label">Collection − Expenses</p>
          <p
            className={`expenses-stat-value${kpis.difference >= 0 ? ' expenses-stat-value--positive' : ' expenses-stat-value--negative'}`}
          >
            {kpis.difference >= 0 ? '+' : '−'}₹
            {Math.abs(kpis.difference).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </p>
          <p className="expenses-stat-trend expenses-stat-trend--neutral">
            Collection ₹{kpis.collection.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            {kpis.expenseRatioPct != null
              ? ` · Expenses ${kpis.expenseRatioPct.toFixed(1)}% of collection`
              : ' · No collection in period'}
          </p>
        </article>
        <article className="expenses-stat-card">
          <div className="expenses-stat-card-top">
            <div className="expenses-stat-icon expenses-stat-icon--purple" aria-hidden>
              #
            </div>
            <button type="button" className="expenses-stat-menu" aria-label="Options">
              ⋯
            </button>
          </div>
          <p className="expenses-stat-label">Entries</p>
          <p className="expenses-stat-value">{kpis.count}</p>
          <p className="expenses-stat-trend expenses-stat-trend--neutral">Recorded expenses</p>
        </article>
        <article className="expenses-stat-card">
          <div className="expenses-stat-card-top">
            <div className="expenses-stat-icon expenses-stat-icon--red" aria-hidden>
              ◆
            </div>
            <button type="button" className="expenses-stat-menu" aria-label="Options">
              ⋯
            </button>
          </div>
          <p className="expenses-stat-label">Top Category</p>
          <p className="expenses-stat-value">{kpis.topCategory.key ? categoryLabel(kpis.topCategory.key) : '—'}</p>
          <p className="expenses-stat-trend expenses-stat-trend--down">
            {kpis.topCategory.amount > 0
              ? `₹${kpis.topCategory.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
              : 'No data yet'}
          </p>
        </article>
      </section>

      <section className="expenses-table-card">
        <div className="expenses-table-toolbar">
          <label className="expenses-search-wrap">
            <span aria-hidden>🔍</span>
            <input
              type="search"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search expenses"
            />
          </label>
          <button
            type="button"
            className={`expenses-btn expenses-filter-btn${filterOpen ? ' is-active' : ''}`}
            onClick={() => setFilterOpen((o) => !o)}
          >
            <span aria-hidden>⏷</span> Filter by
          </button>
        </div>
        {filterOpen ? (
          <div className="expenses-filter-panel">
            <label className="expenses-filter-field">
              <span>Year</span>
              <select value={filterYear} onChange={(e) => onYearChange(e.target.value)} aria-label="Year">
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label className="expenses-filter-field">
              <span>Month</span>
              <select
                value={filterMonth}
                onChange={(e) => onMonthChange(e.target.value)}
                aria-label="Month"
                disabled={Boolean(rangeStart && rangeEnd)}
              >
                <option value="">All months</option>
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="expenses-filter-field">
              <span>From</span>
              <input
                type="date"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                aria-label="Start date"
              />
            </label>
            <label className="expenses-filter-field">
              <span>To</span>
              <input
                type="date"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                aria-label="End date"
              />
            </label>
            <label className="expenses-filter-field">
              <span>Category</span>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} aria-label="Category">
                <option value="ALL">All categories</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="expenses-btn expenses-filter-reset" onClick={resetToCurrentMonth}>
              This month
            </button>
          </div>
        ) : null}

        {loading ? (
          <p className="expenses-loading">Loading expenses…</p>
        ) : (
          <>
            <div className="expenses-table-scroll responsive-table-wrap">
              <table className="expenses-table responsive-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="expenses-table-check"
                        aria-label="Select all on page"
                        checked={pageItems.length > 0 && pageItems.every((e) => selectedIds.has(e.id))}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>Photo</th>
                    <th>Title</th>
                    <th>Account</th>
                    <th>Date</th>
                    <th>Payment</th>
                    <th>Created by</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 ? (
                    <tr className="responsive-table__empty">
                      <td colSpan={8} className="expenses-empty" data-label="">
                        No expenses found
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((expense, idx) => {
                      const amtClass = AMOUNT_CLASSES[idx % AMOUNT_CLASSES.length];
                      const photoUrl = getExpensePhotoUrl(expense);
                      return (
                        <tr
                          key={expense.id}
                          className={selectedIds.has(expense.id) ? 'is-selected' : undefined}
                          onClick={() => setSelectedExpense(expense)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="responsive-table__hide-label" onClick={(e) => e.stopPropagation()} data-label="Select">
                            <input
                              type="checkbox"
                              className="expenses-table-check"
                              checked={selectedIds.has(expense.id)}
                              onChange={() => toggleSelect(expense.id)}
                              aria-label={`Select expense ${expense.id}`}
                            />
                          </td>
                          <td className="responsive-table__hide-label" data-label="Photo">
                            {photoUrl ? (
                              <img src={photoUrl} alt="" className="expenses-thumb" />
                            ) : (
                              <span className="expenses-thumb-placeholder" aria-hidden>
                                —
                              </span>
                            )}
                          </td>
                          <td className="responsive-table__primary" data-label="Title">
                            <span className="expenses-title-cell" title={expense.title}>
                              {expense.title}
                            </span>
                          </td>
                          <td data-label="Category">
                            <span className="expenses-account" title={categoryLabel(expense.category)}>
                              {categoryLabel(expense.category)}
                            </span>
                          </td>
                          <td data-label="Date">{formatExpenseDate(expense.expenseDate)}</td>
                          <td data-label="Payment">{paymentMethodLabel(expense.paymentMethod)}</td>
                          <td className="expenses-col-creator" data-label="Created by" title={creatorDisplayName(expense)}>
                            {creatorDisplayName(expense)}
                          </td>
                          <td className="responsive-table__amount" data-label="Amount">
                            <span className={`expenses-amount ${amtClass}`}>
                              ₹{Number(expense.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <footer className="expenses-table-footer">
              <div className="expenses-pagination">
                <button
                  type="button"
                  className="expenses-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  ←
                </button>
                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`expenses-page-btn${n === page ? ' is-active' : ''}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="expenses-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  →
                </button>
              </div>
              <div className="expenses-footer-meta">
                <span>
                  Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filtered.length)} of {filtered.length}{' '}
                  entries
                </span>
                <label className="expenses-page-size">
                  Show
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
                    <option value={8}>8</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </label>
              </div>
            </footer>
          </>
        )}
      </section>

      {showCreate ? (
        <div className="expenses-modal-overlay" role="presentation" onClick={closeCreateModal}>
          <div
            className="expenses-modal expenses-modal--wide"
            role="dialog"
            aria-labelledby="expense-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="expenses-modal-header">
              <h2 id="expense-create-title">Add new expense</h2>
              <button type="button" className="expenses-modal-close" onClick={closeCreateModal} disabled={submitting}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="expenses-modal-body">
                <div className="expenses-form-grid">
                  <label className="expenses-form-field expenses-form-field--full">
                    <span>Title</span>
                    <input
                      required
                      value={newExpense.title}
                      onChange={(e) => setNewExpense({ ...newExpense, title: e.target.value })}
                    />
                  </label>
                  <label className="expenses-form-field">
                    <span>Category</span>
                    <select
                      value={newExpense.category}
                      onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="expenses-form-field">
                    <span>Amount (₹)</span>
                    <input
                      type="number"
                      step="0.01"
                      required
                      min={0}
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    />
                  </label>
                  <label className="expenses-form-field">
                    <span>Date</span>
                    <input
                      type="date"
                      required
                      value={newExpense.expenseDate}
                      onChange={(e) => setNewExpense({ ...newExpense, expenseDate: e.target.value })}
                    />
                  </label>
                  <label className="expenses-form-field">
                    <span>Payment method</span>
                    <select
                      value={newExpense.paymentMethod}
                      onChange={(e) => setNewExpense({ ...newExpense, paymentMethod: e.target.value })}
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="expenses-form-field expenses-form-field--full">
                    <span>Receipt photo (optional)</span>
                    <div className="expenses-photo-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="expenses-photo-input"
                        id="expense-photo-input"
                      />
                      <label htmlFor="expense-photo-input" className="expenses-photo-label">
                        {photoPreview ? 'Change photo' : 'Choose image'}
                      </label>
                      {photoPreview ? (
                        <div className="expenses-photo-preview-wrap">
                          <img src={photoPreview} alt="Receipt preview" className="expenses-photo-preview" />
                          <button type="button" className="expenses-photo-remove" onClick={clearPhoto}>
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </label>
                  <label className="expenses-form-field expenses-form-field--full">
                    <span>Description</span>
                    <textarea
                      rows={3}
                      value={newExpense.description}
                      onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                    />
                  </label>
                </div>
              </div>
              <div className="expenses-modal-footer">
                <button type="button" className="expenses-btn" onClick={closeCreateModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="expenses-btn expenses-btn--primary" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {selectedExpense ? (
        <div className="expenses-modal-overlay" role="presentation" onClick={() => setSelectedExpense(null)}>
          <div className="expenses-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="expenses-modal-header">
              <h2>{selectedExpense.title}</h2>
              <button type="button" className="expenses-modal-close" onClick={() => setSelectedExpense(null)}>
                ×
              </button>
            </div>
            <div className="expenses-modal-body">
              <div className="expenses-detail-row">
                <span className="label">Amount</span>
                <span>₹{Number(selectedExpense.amount || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="expenses-detail-row">
                <span className="label">Category</span>
                <span>{categoryLabel(selectedExpense.category)}</span>
              </div>
              <div className="expenses-detail-row">
                <span className="label">Date</span>
                <span>{formatExpenseDate(selectedExpense.expenseDate)}</span>
              </div>
              <div className="expenses-detail-row">
                <span className="label">Payment</span>
                <span>{paymentMethodLabel(selectedExpense.paymentMethod)}</span>
              </div>
              <div className="expenses-detail-row">
                <span className="label">Created by</span>
                <span>{creatorDisplayName(selectedExpense)}</span>
              </div>
              {getExpensePhotoUrl(selectedExpense) ? (
                <div className="expenses-detail-photo">
                  <span className="label">Receipt</span>
                  <a href={getExpensePhotoUrl(selectedExpense)!} target="_blank" rel="noopener noreferrer">
                    <img src={getExpensePhotoUrl(selectedExpense)!} alt="Expense receipt" />
                  </a>
                </div>
              ) : null}
              {selectedExpense.description ? (
                <div className="expenses-detail-row">
                  <span className="label">Description</span>
                  <span>{selectedExpense.description}</span>
                </div>
              ) : null}
              <div className="expenses-detail-actions">
                <button
                  type="button"
                  className="expenses-btn expenses-btn--danger"
                  disabled={deleting}
                  onClick={() => handleDelete(selectedExpense.id)}
                >
                  {deleting ? 'Deleting…' : 'Delete expense'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ExpenseManagement;
